import { ApplicationRef, DestroyRef, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Observable, timer } from 'rxjs';

interface VersionResponse {
  version?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly versionUrl = '/version.json';
  private readonly updatePollIntervalMs = 600_000; // 10 minutes

  private currentVersion: string | null = null;
  private hasShownUpdateToast = false;
  private isInitialized = false;

  constructor() { }

  public init(): void {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    this.checkForNewVersion()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (version) => {
          this.currentVersion = version;
          this.bootstrapVersionTracking();
        },
        error: () => {
          // Continue setup even if initial version check fails
          this.bootstrapVersionTracking();
        }
      });
  }

  private bootstrapVersionTracking(): void {
    // Periodic polling once the app is stable
    this.appRef.isStable
      .pipe(
        filter((isStable): isStable is true => isStable),
        take(1),
        switchMap(() => timer(this.updatePollIntervalMs, this.updatePollIntervalMs)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkForNewVersion().subscribe({
          next: (version) => this.handleVersion(version),
          error: () => {
            // Swallow version check errors; app should keep working even if version.json is unavailable
          }
        });
      });
  }

  private checkForNewVersion(): Observable<string | null> {
    const cacheBuster = Date.now().toString();
    const urlWithParam = `${this.versionUrl}?t=${cacheBuster}`;

    return this.http.get<VersionResponse>(urlWithParam).pipe(
      take(1),
      filter((body): body is VersionResponse => !!body),
      map((body): string | null => {
        const version = body.version ?? null;
        return typeof version === 'string' && version.length > 0 ? version : null;
      })
    );
  }

  private handleVersion(remoteVersion: string | null): void {
    if (!remoteVersion) {
      return;
    }

    if (!this.currentVersion) {
      this.currentVersion = remoteVersion;
      return;
    }

    if (remoteVersion !== this.currentVersion) {
      this.currentVersion = remoteVersion;
      this.promptAndReload();
    }
  }

  private promptAndReload(): void {
    if (this.hasShownUpdateToast) {
      return;
    }
    this.hasShownUpdateToast = true;

    const toastRef = this.toastr.info(
      'A new version is available.\nSave your progress then click here to reload the page.',
      'Update available',
      {
        disableTimeOut: true,
        closeButton: true,
        tapToDismiss: false
      }
    );

    toastRef?.onTap?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.toastr.clear();
      window.location.replace(`${window.location.pathname}?v=${Date.now()}`);
    });
  }
}
